<template>
  <div class="flutes-detail" style="margin-top: 6em; margin-bottom: 4em">
    <div class="container" style="max-width: 880px">
      <h1 class="text-center mb-3">irish flutes</h1>
      <p class="text-center text-muted mb-4">
        Hand-turned wooden flutes in the Irish tradition, voiced by hand for a
        warm, responsive tone.
      </p>

      <img
        src="@/assets/img/products/flute-keyed-flatlay.jpg"
        class="w-100 rounded mb-5"
        alt="A keyed blackwood Irish flute, shown disassembled into its sections"
      />

      <p class="mb-4">
        I make traditional flutes in D. Other Keys may be available on request.
      </p>

      <h2 class="mb-3">Features</h2>
      <ul class="feature-list mb-5">
        <li>
          Keyless or keyed — up to six keys, and keys can be ordered
          individually, so you get exactly the setup you want.
        </li>
        <li>A Rudall-style bore.</li>
        <li>
          Slightly undercut tone holes. The G and E holes can be offset for a
          comfortable reach, and the amount of offset is configurable.
        </li>
        <li>
          A CNC-milled, fully configurable embouchure: front and back
          undercutting, ovality, a chamfer on the side edges, and its length and
          width — all to your preference.
        </li>
        <li>
          Your choice of head: a slim head with a lip-plate, or a more
          traditional cylindrical head.
        </li>
        <li>Tunable with a tuning slide and an easily adjustable cork.</li>
        <li>A brass counterweight at the foot end for good balance.</li>
        <li>
          Preferred woods are grenadilla<CitesInfoComponent />
          and olive; other woods are possible by arrangement.
        </li>
      </ul>

      <h2 class="mb-3">Construction</h2>
      <p class="mb-4">
        Every flute combines hand craftsmanship with CNC-controlled precision.
        The features that rely on exact geometry are CNC-milled — the embouchure
        hole, the finger holes and the seats for the keys — for consistent,
        repeatable results.
      </p>
      <p class="mb-5">
        Everything that gives the flute its voice and feel is then done by hand:
        the inner and outer contours are shaped and fine-finished manually, and
        each key is made from scratch — turned, forged and soldered.
      </p>

      <h2 class="mb-3">Materials</h2>
      <p class="mb-4">
        The standard woods are grenadilla<CitesInfoComponent />
        or olive. Other close-grained hardwoods are available as well — for
        example katalox, padauk, leadwood or African ebony.
      </p>
      <p class="mb-5">Keys and rings are made of brass.</p>

      <h2 class="mb-3">Prices</h2>
      <p>
        There is no online shop here — to order, just
        <LinkComponent href="/#contact" link-text="contact me" />.
      </p>
      <p class="mb-4">
        Prices are for the build; the wood is added on top and each key is
        priced individually — so you only pay for the setup you choose.
      </p>
      <table class="table align-middle">
        <tbody>
          <tr>
            <th scope="row" class="fw-normal">Flute, keyless — the build</th>
            <td class="text-end">750&nbsp;€</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">Wood — olive</th>
            <td class="text-end">+&nbsp;45&nbsp;€</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">
              Wood — grenadilla or other hardwoods
            </th>
            <td class="text-end">by arrangement</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">Each key</th>
            <td class="text-end">165&nbsp;€</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">Cylindrical head</th>
            <td class="text-end">included</td>
          </tr>
          <tr>
            <th scope="row" class="fw-normal">
              Slim head with a milled lip-plate
            </th>
            <td class="text-end">+&nbsp;50&nbsp;€</td>
          </tr>
        </tbody>
      </table>
      <p class="text-muted mb-5">
        For example, a keyless flute in olive comes to 795&nbsp;€; a fully keyed
        flute (six keys) is 1,740&nbsp;€ plus the wood.
      </p>

      <h2 class="mb-3">Gallery</h2>
      <ImageCarouselComponent
        id="fluteGallery"
        :images="galleryImages"
        class="mb-5"
      />

      <p class="text-center">
        <LinkComponent href="/#contact" link-text="Contact me about a flute" />
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LinkComponent from "@/components/util/LinkComponent.vue";
import ImageCarouselComponent from "@/components/util/ImageCarouselComponent.vue";
import CitesInfoComponent from "@/components/general/CitesInfoComponent.vue";
import fullPadauk from "@/assets/img/products/gallery/flute-full-padauk.jpg";
import toneHoles from "@/assets/img/products/gallery/flute-tone-holes.jpg";
import keys from "@/assets/img/products/gallery/flute-keys.jpg";
import embouchure from "@/assets/img/products/gallery/flute-embouchure.jpg";
import tuning from "@/assets/img/products/gallery/flute-tuning.jpg";
import head from "@/assets/img/products/gallery/flute-head.jpg";
import grenadillaHead from "@/assets/img/products/gallery/flute-grenadilla-head.jpg";
import grenadillaEmbouchure from "@/assets/img/products/gallery/flute-grenadilla-embouchure.jpg";
import grenadillaCrown from "@/assets/img/products/gallery/flute-grenadilla-crown.jpg";
import grenadillaHeadLh from "@/assets/img/products/gallery/flute-grenadilla-head-lh.jpg";

export default defineComponent({
  name: "FlutesView",
  components: { LinkComponent, ImageCarouselComponent, CitesInfoComponent },
  data() {
    return {
      galleryImages: [
        {
          src: fullPadauk,
          caption: "Keyless flute in African padauk, lip plate in blackwood",
        },
        {
          src: toneHoles,
          caption: "Right hand part (padauk) with offset E hole",
        },
        { src: keys, caption: "Hand-made brass keywork (African blackwood)" },
        {
          src: embouchure,
          caption: "Embouchure hole",
        },
        {
          src: tuning,
          caption: "The head with adjustable tuning cork",
        },
        {
          src: head,
          caption: "Barrel with maker's mark (African blackwood)",
        },
        {
          src: grenadillaHead,
          caption: "Head and barrel of a grenadilla flute with brass rings",
        },
        {
          src: grenadillaEmbouchure,
          caption: "The embouchure of a grenadilla flute",
        },
        {
          src: grenadillaCrown,
          caption: "Having fun with the crown (grenadilla)",
        },
        {
          src: grenadillaHeadLh,
          caption:
            "Head, barrel and part of the left-hand section (grenadilla)",
        },
      ],
    };
  },
});
</script>

<style scoped>
.feature-list li {
  margin-bottom: 0.6rem;
}
</style>

<style src="@/assets/css/style.css"></style>
